import type { MenuTheme, MenuThemeId } from '@/types'

// ============================================
// PREMIUM MENU THEMES - Designed to WOW customers
// Each theme has a completely unique visual identity
// ============================================

export const MENU_THEMES: Record<MenuThemeId, MenuTheme> = {
  // ----------------------------------------
  // CLASSIC ORANGE - Default Qorder theme
  // Clean, modern, professional
  // ----------------------------------------
  classic: {
    id: 'classic',
    name: 'Classic Qorder',
    description: 'Pulito e professionale',
    emoji: '🍊',
    // Colors
    primaryColor: '#F97316',
    secondaryColor: '#FB923C',
    accentColor: '#EF4444',
    backgroundColor: '#0a0a0a',
    cardBackground: '#141414',
    cardBackgroundHover: '#1a1a1a',
    textColor: '#ffffff',
    textSecondary: '#a1a1aa',
    mutedColor: '#71717a',
    borderColor: '#27272a',
    // Gradients
    heroGradient: 'linear-gradient(135deg, #0f0f1a 0%, #1a1025 50%, #0a0a12 100%)',
    cardGradient: 'linear-gradient(145deg, #181818 0%, #141414 100%)',
    buttonGradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    // Visual effects
    glowColor: 'rgba(249, 115, 22, 0.4)',
    shadowColor: 'rgba(249, 115, 22, 0.2)',
    // Background pattern
    backgroundPattern: 'radial',
    backgroundPatternOpacity: 0.03,
    // Animation style
    animationStyle: 'smooth',
    // Card style
    cardStyle: 'modern',
    cardBorderRadius: '16px',
    // Special decorations
    hasParticles: false,
    hasFloatingElements: false,
    decorativeElement: 'orbs',
  },

  // ----------------------------------------
  // LUXURY GOLD - Ultra premium, high-end restaurants
  // Gold accents, marble textures, elegant animations
  // ----------------------------------------
  luxury: {
    id: 'luxury',
    name: 'Luxury Gold',
    description: 'Eleganza senza tempo',
    emoji: '✨',
    // Colors - Rich gold and deep black
    primaryColor: '#D4AF37',
    secondaryColor: '#F4D03F',
    accentColor: '#C5A028',
    backgroundColor: '#0a0908',
    cardBackground: '#141210',
    cardBackgroundHover: '#1a1814',
    textColor: '#FAF8F5',
    textSecondary: '#C9C5BD',
    mutedColor: '#8A8680',
    borderColor: '#2a2720',
    // Gradients - Golden luxury
    heroGradient: 'linear-gradient(135deg, #0a0908 0%, #1a1510 40%, #0d0b09 100%)',
    cardGradient: 'linear-gradient(145deg, #1a1814 0%, #141210 50%, #0f0d0b 100%)',
    buttonGradient: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 50%, #C5A028 100%)',
    // Visual effects
    glowColor: 'rgba(212, 175, 55, 0.5)',
    shadowColor: 'rgba(212, 175, 55, 0.3)',
    // Background pattern - Marble texture effect
    backgroundPattern: 'marble',
    backgroundPatternOpacity: 0.08,
    // Animation style
    animationStyle: 'elegant',
    // Card style
    cardStyle: 'luxury',
    cardBorderRadius: '12px',
    // Special decorations
    hasParticles: true,
    hasFloatingElements: true,
    decorativeElement: 'sparkles',
  },

  // ----------------------------------------
  // NEON CYBERPUNK - Modern, tech-forward, vibrant
  // Neon colors, glitch effects, futuristic vibes
  // ----------------------------------------
  neon: {
    id: 'neon',
    name: 'Neon Night',
    description: 'Futuristico e vibrante',
    emoji: '🌃',
    // Colors - Electric neon
    primaryColor: '#00F5FF',
    secondaryColor: '#FF00E5',
    accentColor: '#39FF14',
    backgroundColor: '#050510',
    cardBackground: '#0a0a18',
    cardBackgroundHover: '#0f0f22',
    textColor: '#ffffff',
    textSecondary: '#B8B8D0',
    mutedColor: '#6B6B8A',
    borderColor: '#1a1a30',
    // Gradients - Neon glow
    heroGradient: 'linear-gradient(135deg, #050510 0%, #0a0520 50%, #100525 100%)',
    cardGradient: 'linear-gradient(145deg, #0f0f22 0%, #0a0a18 100%)',
    buttonGradient: 'linear-gradient(135deg, #00F5FF 0%, #00D4E5 50%, #00B8D4 100%)',
    // Visual effects
    glowColor: 'rgba(0, 245, 255, 0.6)',
    shadowColor: 'rgba(255, 0, 229, 0.4)',
    // Background pattern - Grid lines
    backgroundPattern: 'grid',
    backgroundPatternOpacity: 0.1,
    // Animation style
    animationStyle: 'energetic',
    // Card style
    cardStyle: 'neon',
    cardBorderRadius: '8px',
    // Special decorations
    hasParticles: true,
    hasFloatingElements: true,
    decorativeElement: 'scanlines',
  },

  // ----------------------------------------
  // RUSTIC WOOD - Warm, cozy, traditional
  // Wood textures, warm colors, homey feel
  // ----------------------------------------
  rustic: {
    id: 'rustic',
    name: 'Rustico',
    description: 'Caldo e accogliente',
    emoji: '🪵',
    // Colors - Warm wood tones
    primaryColor: '#CD853F',
    secondaryColor: '#DEB887',
    accentColor: '#8B4513',
    backgroundColor: '#1a1410',
    cardBackground: '#241c14',
    cardBackgroundHover: '#2e2418',
    textColor: '#FDF5E8',
    textSecondary: '#D4C4A8',
    mutedColor: '#9A8B70',
    borderColor: '#3d3020',
    // Gradients - Warm amber
    heroGradient: 'linear-gradient(135deg, #1a1410 0%, #251a12 50%, #1f1610 100%)',
    cardGradient: 'linear-gradient(145deg, #2a2018 0%, #241c14 100%)',
    buttonGradient: 'linear-gradient(135deg, #CD853F 0%, #B8732D 50%, #A0631F 100%)',
    // Visual effects
    glowColor: 'rgba(205, 133, 63, 0.4)',
    shadowColor: 'rgba(139, 69, 19, 0.3)',
    // Background pattern - Wood grain
    backgroundPattern: 'wood',
    backgroundPatternOpacity: 0.15,
    // Animation style
    animationStyle: 'gentle',
    // Card style
    cardStyle: 'rustic',
    cardBorderRadius: '12px',
    // Special decorations
    hasParticles: false,
    hasFloatingElements: false,
    decorativeElement: 'leaves',
  },

  // ----------------------------------------
  // OCEAN WAVE - Fresh, seafood, coastal
  // Blue gradients, wave animations, sea vibes
  // ----------------------------------------
  ocean: {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Fresco come il mare',
    emoji: '🌊',
    // Colors - Deep ocean
    primaryColor: '#0EA5E9',
    secondaryColor: '#38BDF8',
    accentColor: '#06B6D4',
    backgroundColor: '#040810',
    cardBackground: '#081018',
    cardBackgroundHover: '#0c1420',
    textColor: '#F0F9FF',
    textSecondary: '#BAE6FD',
    mutedColor: '#7DD3FC',
    borderColor: '#164E63',
    // Gradients - Ocean depth
    heroGradient: 'linear-gradient(180deg, #040810 0%, #0c1a28 50%, #082030 100%)',
    cardGradient: 'linear-gradient(145deg, #0c1822 0%, #081018 100%)',
    buttonGradient: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 50%, #0369A1 100%)',
    // Visual effects
    glowColor: 'rgba(14, 165, 233, 0.5)',
    shadowColor: 'rgba(6, 182, 212, 0.3)',
    // Background pattern - Waves
    backgroundPattern: 'waves',
    backgroundPatternOpacity: 0.12,
    // Animation style
    animationStyle: 'flowing',
    // Card style
    cardStyle: 'glass',
    cardBorderRadius: '20px',
    // Special decorations
    hasParticles: true,
    hasFloatingElements: true,
    decorativeElement: 'bubbles',
  },

  // ----------------------------------------
  // CHRISTMAS MAGIC - Festive, holiday spirit
  // Red & green, snow particles, magical feel
  // ----------------------------------------
  christmas: {
    id: 'christmas',
    name: 'Natale Magico',
    description: 'Atmosfera festiva',
    emoji: '🎄',
    // Colors - Christmas classic
    primaryColor: '#DC2626',
    secondaryColor: '#EF4444',
    accentColor: '#16A34A',
    backgroundColor: '#0f0a0a',
    cardBackground: '#1a1212',
    cardBackgroundHover: '#221818',
    textColor: '#FEF2F2',
    textSecondary: '#FECACA',
    mutedColor: '#F87171',
    borderColor: '#3f1f1f',
    // Gradients - Christmas warmth
    heroGradient: 'linear-gradient(135deg, #0f0a0a 0%, #1a0f0f 30%, #0f1a0f 70%, #0a0f0a 100%)',
    cardGradient: 'linear-gradient(145deg, #1f1515 0%, #1a1212 100%)',
    buttonGradient: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%)',
    // Visual effects
    glowColor: 'rgba(220, 38, 38, 0.5)',
    shadowColor: 'rgba(22, 163, 74, 0.3)',
    // Background pattern - Snow
    backgroundPattern: 'snow',
    backgroundPatternOpacity: 0.2,
    // Animation style
    animationStyle: 'magical',
    // Card style
    cardStyle: 'festive',
    cardBorderRadius: '16px',
    // Special decorations
    hasParticles: true,
    hasFloatingElements: true,
    decorativeElement: 'snowflakes',
  },
}

export const THEME_LIST = Object.values(MENU_THEMES)

export const getTheme = (themeId?: MenuThemeId): MenuTheme => {
  return MENU_THEMES[themeId || 'classic'] || MENU_THEMES.classic
}

// Helper to generate CSS variables for a theme
export const getThemeCSSVariables = (theme: MenuTheme): Record<string, string> => {
  return {
    '--theme-primary': theme.primaryColor,
    '--theme-secondary': theme.secondaryColor,
    '--theme-accent': theme.accentColor,
    '--theme-bg': theme.backgroundColor,
    '--theme-card': theme.cardBackground,
    '--theme-card-hover': theme.cardBackgroundHover,
    '--theme-text': theme.textColor,
    '--theme-text-secondary': theme.textSecondary,
    '--theme-muted': theme.mutedColor,
    '--theme-border': theme.borderColor,
    '--theme-glow': theme.glowColor,
    '--theme-shadow': theme.shadowColor,
  }
}
